import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import { SERVICES } from "../data/mock";
import { fetchReferences } from "../lib/api";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import ReferenceCard from "../components/ReferenceCard";

const References = () => {
  const { lang } = useLanguage();
  const t = translations[lang].references;
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");

  useEffect(() => {
    fetchReferences()
      .then(setRefs)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? refs : refs.filter((r) => r.service === active)),
    [active, refs]
  );

  return (
    <>
      <Seo title={t.pageTitle} description={t.pageDesc} path="/referanslar" lang={lang} />
      <PageHero title={t.pageTitle} desc={t.pageDesc} />

      <section className="bg-slate-50 py-16 lg:py-24" data-testid="references-page">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-3 mb-10">
            {[{ id: "all", tag: { tr: "Tümü", en: "All" } }, ...SERVICES].map((s) => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  active === s.id ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`references-filter-${s.id}`}
              >
                {s.tag[lang]}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20 text-slate-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((r, i) => (
                <Reveal key={r.id} delay={(i % 3) * 90}>
                  <ReferenceCard r={r} />
                </Reveal>
              ))}
              {filtered.length === 0 && <p className="text-slate-400 col-span-full py-10 text-center">{t.empty}</p>}
            </div>
          )}

          <div className="mt-16 rounded-3xl bg-navy-900 px-8 py-12 lg:px-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h2 className="font-display font-bold text-2xl lg:text-3xl text-white">{t.ctaTitle}</h2>
              <p className="mt-3 text-slate-300">{t.ctaDesc}</p>
            </div>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold px-7 py-3.5 rounded-xl transition-colors shrink-0"
              data-testid="references-cta"
            >
              {t.ctaButton} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default References;
